"use client";

import { useTranslations } from "@i18n/next";
import { HallowABFlag, useMixpanel } from "@packages/ab";
import { HallowAnalyticsEvent, useAnalytics } from "@packages/analytics";
import { useRedirectToSubscribe } from "@packages/kids";
import { usePlayer } from "@packages/media";
import type { Prayer, Track } from "@packages/sdk";
import { useRedirectToAuth, useSession } from "@packages/sdk";
import { Button, PlayIcon, SubscriptionIcon } from "@packages/ui";
import { HallowDataTestId } from "@tools/playwright/global";
import { Suspense } from "react";

type PrayerPlayButtonProps = {
  prayer: Prayer;
  track: Pick<Track, "id" | "paid">;
  isSong?: boolean;
};

export const PrayerPlayButton = ({
  prayer,
  track,
  isSong,
}: PrayerPlayButtonProps) => {
  const t = useTranslations();
  const { status } = useSession();
  const player = usePlayer();
  const redirectToSubscribe = useRedirectToSubscribe();
  const redirectToAuth = useRedirectToAuth();
  const analytics = useAnalytics();
  const { flags } = useMixpanel();
  const isTargetedWebHalfOffFirstYear =
    flags?.[HallowABFlag.WebHalfOffFirstYear]?.isTargeted;

  if (!track) {
    return null;
  }

  const needsToSubscribeToListen = !Boolean(prayer?.has_access);

  const playSession = async () => {
    if (needsToSubscribeToListen) {
      analytics.track({
        event: prayer?.has_video
          ? HallowAnalyticsEvent.TappedSubscribeToWatch
          : HallowAnalyticsEvent.TappedSubscribeToListen,
        properties: {
          prayer_id: prayer?.id ?? null,
          screen_name: "session_detail",
        },
      });
      return redirectToSubscribe({
        redirectBackOnSubscription: true,
        forContent: prayer
          ? { title: prayer.title, images: prayer.images }
          : null,
      });
    } else if (status === "authenticated") {
      analytics.track({
        event: HallowAnalyticsEvent.TappedPlaySession,
        properties: {
          prayer_id: prayer?.id ?? null,
          screen_name: "session_detail",
        },
      });

      // it's possible someone reached this page via deeplink or manual URL editing, so it could still be a song
      // page. We need to act appropriately.
      if (isSong) {
        await player?.playNew({
          type: "album",
          id: prayer.collection_id,
          // these are only used for albums
          target: prayer.id,
        });
      } else {
        await player?.playNew({ type: "audio", id: track.id });
      }
    } else {
      analytics.track({
        event: prayer?.has_video
          ? HallowAnalyticsEvent.TappedWatchForFree
          : HallowAnalyticsEvent.TappedListenForFree,
        properties: {
          prayer_id: prayer?.id ?? null,
          screen_name: "session_detail",
        },
      });
      return redirectToAuth();
    }
  };

  return (
    <Suspense>
      <Button
        data-testid={HallowDataTestId.ContentCTAButton}
        isFullWidth={true}
        onClickCapture={playSession}
        size="l"
        startIcon={
          needsToSubscribeToListen ? (
            <SubscriptionIcon.Filled />
          ) : (
            <PlayIcon.Small />
          )
        }
        variant={"secondary"}
      >
        {needsToSubscribeToListen && isTargetedWebHalfOffFirstYear
          ? t("sml_redeem_app_cta", { 0: 50 })
          : t(
              needsToSubscribeToListen
                ? prayer?.has_video
                  ? "button_subscribe_to_watch"
                  : "button_subscribe_to_listen"
                : status === "authenticated"
                  ? "play_session"
                  : prayer?.has_video
                    ? "button_watch_for_free"
                    : "button_listen_for_free",
            )}
      </Button>
    </Suspense>
  );
};
